/**
 * Cash-in settlement.
 *
 * Company treasury for retail cash-in providers. EasyPay is the live
 * counterparty. Further cash-in providers use the same screen when their
 * terms row is added. This is not channel-partner settlement and it is not
 * a supplier-float balance.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const api = axios.create({ baseURL: '/api/v1/admin' });
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

interface Provider {
  id: number;
  counterpartyCode: string;
  productCode: string;
  displayName: string;
  legalName: string;
  settlementLagBusinessDays: number;
  settlementMode: string;
  prefundedActive: boolean;
  calendarTz: string;
  active: boolean;
}

interface Invoice {
  id: number;
  businessDate: string;
  dueDate: string;
  lockedLagBusinessDays: number;
  lockedSettlementMode: string;
  paidPinCount: number;
  grossTenderCents: number;
  channelFeeCents: number;
  cashHandlingCents: number;
  netDueCents: number;
  status: string;
  journalReference: string | null;
  makerUserId: number | null;
  evidenceKey: string | null;
  batchRef: string;
}

interface EvidenceCandidate {
  evidenceKey: string;
  source: string;
  amountCents: number;
  valueDate: string;
  suspenseParked: boolean;
}

interface BreakRow {
  type: string;
  side?: string;
  count?: number;
}

interface AuditData {
  terms: Provider;
  invoice: Invoice | null;
  statement: string | null;
  evidence: { status: string; candidates: EvidenceCandidate[] };
  breaks: BreakRow[];
  lineMatch?: { fileCount: number; ourCount: number; breakCount: number } | null;
  ledgerNetCents: number | null;
  posted: boolean;
}

function zar(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const rands = Math.floor(abs / 100).toLocaleString('en-ZA');
  const rem = String(abs % 100).padStart(2, '0');
  return `${sign}R${rands}.${rem}`;
}

const BREAK_LABELS: Record<string, string> = {
  missing_sof: 'Statement of funds file is missing',
  sof_vs_ledger: 'Statement of funds does not match the ledger',
  due_date_no_credit: 'Due date has passed and no treasury credit matched',
  credit_without_invoice: 'Treasury credit has no cash-in invoice',
  amount_mismatch: 'Amount does not match',
  ambiguous_credit: 'More than one treasury credit matches this amount and date',
  line_not_in_file: 'A paid PIN is missing from the statement file',
  line_not_in_ledger: 'A statement line has no matching paid PIN',
  line_amount_mismatch: 'A statement line amount does not match the paid PIN',
};

export const CashInSettlementOverlay: React.FC = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selected, setSelected] = useState<string>('easypay:easypay_topup');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [businessDate, setBusinessDate] = useState('');
  const [lagInput, setLagInput] = useState('2');
  const [audit, setAudit] = useState<AuditData | null>(null);
  const [evidenceKey, setEvidenceKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const provider = providers.find((row) => `${row.counterpartyCode}:${row.productCode}` === selected) || null;

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/cash-in-settlement/providers');
      const rows: Provider[] = response.data?.data?.providers || [];
      setProviders(rows);
      if (rows.length > 0 && !rows.some((row) => `${row.counterpartyCode}:${row.productCode}` === selected)) {
        setSelected(`${rows[0].counterpartyCode}:${rows[0].productCode}`);
      }
    } catch (err) {
      setError('Cash-in settlement could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  const loadInvoices = useCallback(async (current: Provider | null) => {
    if (!current) return;
    const response = await api.get('/cash-in-settlement/invoices', {
      params: { counterparty: current.counterpartyCode, product: current.productCode },
    });
    setInvoices(response.data?.data?.invoices || []);
    setLagInput(String(current.settlementLagBusinessDays));
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    if (!provider) return;
    loadInvoices(provider).catch(() => setError('Invoices could not be loaded.'));
  }, [provider, loadInvoices]);

  async function runAudit() {
    if (!provider || !businessDate) return;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post('/cash-in-settlement/audit', {
        counterparty: provider.counterpartyCode,
        product: provider.productCode,
        businessDate,
      });
      const data = response.data.data as AuditData;
      setAudit(data);
      setEvidenceKey('');
      await loadInvoices(provider);
    } catch (err) {
      setError('The daily audit could not be built.');
    } finally {
      setWorking(false);
    }
  }

  async function saveLag() {
    if (!provider || user?.role !== 'admin') return;
    setWorking(true);
    setError('');
    try {
      await api.patch('/cash-in-settlement/terms', {
        counterparty: provider.counterpartyCode,
        product: provider.productCode,
        settlementLagBusinessDays: Number(lagInput),
      });
      setNotice('Lag saved. Invoices already issued are unchanged.');
      await loadProviders();
    } catch (err) {
      setError('The settlement lag could not be saved.');
    } finally {
      setWorking(false);
    }
  }

  async function propose() {
    if (!audit?.invoice || !evidenceKey) return;
    setWorking(true);
    setError('');
    try {
      await api.post(`/cash-in-settlement/invoices/${audit.invoice.id}/propose`, { evidenceKey });
      setNotice('Match proposed. A different admin must approve it.');
      await runAudit();
    } catch (err) {
      setError('The match could not be proposed.');
    } finally {
      setWorking(false);
    }
  }

  async function approve() {
    if (!audit?.invoice) return;
    setWorking(true);
    setError('');
    try {
      const response = await api.post(`/cash-in-settlement/invoices/${audit.invoice.id}/approve`);
      const posted = response.data?.data?.posted === true;
      setNotice(posted
        ? `Settlement posted ${response.data.data.reference}.`
        : 'No second journal was posted.');
      await runAudit();
    } catch (err) {
      setError('The settlement was not posted.');
    } finally {
      setWorking(false);
    }
  }

  const invoice = audit?.invoice || null;
  const canApprove = Boolean(
    user
    && user.role === 'admin'
    && invoice
    && invoice.makerUserId
    && invoice.makerUserId !== user.id
    && !invoice.journalReference
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Cash-in settlement</h1>
        <p className="mt-2 max-w-3xl text-sm text-gray-600">
          Treasury receipts for retail cash-in. The wallet is credited when the till pays.
          This screen invoices the net Lesaka must pay into the Standard Bank treasury account
          and records that receipt. It is not partner settlement and it is not a supplier float.
        </p>
      </div>

      {loading && <div className="mymoolah-card p-6 text-sm text-gray-600">Loading cash-in providers…</div>}
      {error && <div className="mymoolah-card border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
      {notice && <div className="mymoolah-card border border-green-200 bg-green-50 p-4 text-sm text-green-900">{notice}</div>}

      {!loading && providers.length === 0 && (
        <div className="mymoolah-card p-6 text-sm text-gray-600">
          No cash-in providers are configured. Apply the cash-in settlement migration, then reload.
        </div>
      )}

      {!loading && providers.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {providers.map((row) => {
            const key = `${row.counterpartyCode}:${row.productCode}`;
            const isSelected = key === selected;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setSelected(key); setAudit(null); }}
                className={`mymoolah-card p-4 text-left ${isSelected ? 'ring-2 ring-[var(--primary,#86BE41)]' : ''}`}
              >
                <div className="text-sm font-semibold text-gray-900">{row.legalName}</div>
                <div className="mt-1 text-xs text-gray-500">{row.displayName} · {row.productCode}</div>
                <div className="mt-3 text-sm text-gray-700">
                  Mode: {row.settlementMode === 'receivable' ? 'Receivable' : row.settlementMode}
                  {' · '}
                  Lag {row.settlementLagBusinessDays} business days
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  Prefunded float: {row.prefundedActive ? 'Active' : 'Not active'}
                </div>
              </button>
            );
          })}
          <div className="mymoolah-card border border-dashed p-4 text-sm text-gray-500">
            Further cash-in providers will appear here. Each one keeps its own settlement terms.
            Prefunded float settlement is not built.
          </div>
        </div>
      )}

      {provider && (
        <div className="mymoolah-card space-y-4 p-6">
          <h2 className="text-lg font-semibold text-gray-900">Daily audit</h2>
          <p className="text-sm text-gray-600">
            Calendar {provider.calendarTz}. Weekends and South African public holidays are excluded from the lag.
            The lag locks onto each statement when it is created.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-gray-700">
              Business date
              <input
                type="date"
                value={businessDate}
                onChange={(event) => setBusinessDate(event.target.value)}
                className="mt-1 block rounded border border-gray-300 px-3 py-2"
              />
            </label>
            <button
              type="button"
              onClick={runAudit}
              disabled={working || !businessDate}
              className="rounded bg-[var(--primary,#86BE41)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {working ? 'Working…' : 'Build statement'}
            </button>
            {user?.role === 'admin' && (
              <label className="text-sm text-gray-700">
                Lag for new statements
                <span className="mt-1 flex gap-2">
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={lagInput}
                    onChange={(event) => setLagInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 px-3 py-2"
                  />
                  <button type="button" onClick={saveLag} disabled={working} className="rounded border px-3 py-2">
                    Save lag
                  </button>
                </span>
              </label>
            )}
          </div>

          {audit && (
            <div className="space-y-4 border-t pt-4">
              {!invoice && (
                <p className="text-sm text-gray-600">
                  No paid cash-in PINs for this business date. Unallocated customer deposits stay on Deposits.
                  This screen does not pay them to a wallet.
                </p>
              )}
              {invoice && (
                <dl className="grid gap-2 text-sm md:grid-cols-2">
                  <div>Paid count: {invoice.paidPinCount}</div>
                  <div>Gross tender: {zar(invoice.grossTenderCents)}</div>
                  <div>Channel fee retained by the counterparty: {zar(invoice.channelFeeCents)}</div>
                  <div>Cash handling: {zar(invoice.cashHandlingCents)}</div>
                  <div>Net due: {zar(invoice.netDueCents)}</div>
                  <div>Due date: {invoice.dueDate} (lag {invoice.lockedLagBusinessDays} locked)</div>
                  <div>Ledger net for the day&apos;s PINs: {audit.ledgerNetCents == null ? '—' : zar(audit.ledgerNetCents)}</div>
                  <div>Status: {invoice.status}{invoice.journalReference ? ` · ${invoice.journalReference}` : ''}</div>
                </dl>
              )}

              {audit.lineMatch && audit.lineMatch.fileCount > 0 && (
                <p className="text-sm text-gray-700">
                  Statement file lines: {audit.lineMatch.fileCount}. Paid PINs: {audit.lineMatch.ourCount}.
                  {audit.lineMatch.breakCount === 0
                    ? ' Each line matches a paid PIN.'
                    : ' The line breaks below must be cleared before approval.'}
                </p>
              )}
              {audit.breaks.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">
                  {audit.breaks.map((row) => (
                    <li key={`${row.type}-${row.side || ''}`}>
                      {BREAK_LABELS[row.type] || row.type}
                      {row.count ? ` (${row.count})` : ''}
                    </li>
                  ))}
                </ul>
              )}
              {audit.breaks.length === 0 && invoice && (
                <p className="text-sm text-gray-600">No open breaks on this statement.</p>
              )}

              {audit.statement && (
                <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-gray-50 p-4 text-sm text-gray-800">{audit.statement}</pre>
              )}

              {invoice && !invoice.journalReference && (
                <fieldset className="space-y-2">
                  <legend className="text-sm font-semibold text-gray-900">Treasury credit</legend>
                  <p className="text-sm text-gray-600">
                    Match on amount and value date. The bank narrative is not confirmed yet.
                    SOAP evidence is used first. The statement line is the backup.
                    Approval does not credit a wallet.
                  </p>
                  {(audit.evidence.candidates || []).length === 0 && (
                    <p className="text-sm text-gray-600">No matching treasury credit. Nothing will be posted.</p>
                  )}
                  {(audit.evidence.candidates || []).map((candidate) => (
                    <label key={candidate.evidenceKey} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="evidence"
                        value={candidate.evidenceKey}
                        checked={evidenceKey === candidate.evidenceKey}
                        onChange={() => setEvidenceKey(candidate.evidenceKey)}
                      />
                      {candidate.source.toUpperCase()} · {zar(candidate.amountCents)} · {candidate.valueDate}
                      {candidate.suspenseParked ? ' · parked unallocated' : ' · treasury receipt not yet journaled to the receivable'}
                    </label>
                  ))}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={propose}
                      disabled={working || !evidenceKey || audit.evidence.status !== 'one'}
                      className="rounded border px-4 py-2 text-sm disabled:opacity-50"
                    >
                      Propose match
                    </button>
                    <button
                      type="button"
                      onClick={approve}
                      disabled={working || !canApprove}
                      className="rounded bg-[var(--mymoolah-blue,#2D8CCA)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      Approve settlement
                    </button>
                  </div>
                  {invoice.makerUserId === user?.id && (
                    <p className="text-sm text-gray-600">You proposed this match. A different admin approves it.</p>
                  )}
                </fieldset>
              )}
            </div>
          )}
        </div>
      )}

      {invoices.length > 0 && (
        <div className="mymoolah-card overflow-x-auto p-6">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Issued statements</h2>
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="py-2 pr-4">Business date</th>
                <th className="py-2 pr-4">Due</th>
                <th className="py-2 pr-4">Net due</th>
                <th className="py-2 pr-4">Lag</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="py-2 pr-4">{row.businessDate}</td>
                  <td className="py-2 pr-4">{row.dueDate}</td>
                  <td className="py-2 pr-4">{zar(row.netDueCents)}</td>
                  <td className="py-2 pr-4">{row.lockedLagBusinessDays}</td>
                  <td className="py-2">{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
